import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Prediction } from '../types/auction.types';
import { format } from 'date-fns';

const Predictions: React.FC = () => {
  const { currentUser } = useAuth();
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'won' | 'lost'>('all');

  useEffect(() => {
    if (currentUser) {
      loadPredictions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const loadPredictions = async () => {
    if (!currentUser) return;

    try {
      const predictionsRef = collection(db, 'predictions');
      const q = query(
        predictionsRef,
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      
      const predictionsList: Prediction[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        predictionsList.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          settledAt: data.settledAt?.toDate() || null,
        } as Prediction);
      });

      setPredictions(predictionsList);
    } catch (error) {
      console.error('Error loading predictions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPredictions = predictions.filter(pred => {
    if (filter === 'all') return true;
    return pred.status === filter;
  });

  const stats = {
    total: predictions.length,
    pending: predictions.filter(p => p.status === 'pending').length,
    won: predictions.filter(p => p.status === 'won').length,
    lost: predictions.filter(p => p.status === 'lost').length,
    totalWagered: predictions.reduce((sum, p) => sum + (p.wagerAmount || 0), 0),
    totalEarned: predictions.reduce((sum, p) => sum + (p.ringersEarned || 0), 0),
    netProfit: predictions.reduce((sum, p) => {
      if (p.status === 'won') {
        return sum + ((p.ringersEarned || 0) - (p.wagerAmount || 0));
      } else if (p.status === 'lost') {
        return sum - (p.wagerAmount || 0);
      }
      return sum;
    }, 0),
    accuracy: predictions.filter(p => p.actualPrice).length > 0
      ? (predictions.filter(p => p.status === 'won').length / 
         predictions.filter(p => p.actualPrice).length * 100).toFixed(1)
      : '0.0'
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: 'bg-gray-100 text-gray-800',
      won: 'bg-green-100 text-green-800',
      lost: 'bg-red-100 text-red-800'
    };
    return badges[status as keyof typeof badges] || badges.pending;
  };

  const getOutcomeIcon = (outcomeType?: string) => {
    switch (outcomeType) {
      case 'outside_range_hit':
        return '⚡';
      case 'inside_range':
        return '✓';
      case 'went_over':
        return '❌';
      case 'outside_range_miss':
        return '😞';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading predictions...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">My Predictions</h1>
        <Link to="/auctions" className="btn-primary">
          Make New Prediction
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-6 gap-4">
        <div className="card">
          <div className="text-sm text-gray-600 mb-1">Total Bets</div>
          <div className="text-3xl font-bold text-primary-600">{stats.total}</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-600 mb-1">Pending</div>
          <div className="text-3xl font-bold text-gray-600">{stats.pending}</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-600 mb-1">Won</div>
          <div className="text-3xl font-bold text-green-600">{stats.won}</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-600 mb-1">Win Rate</div>
          <div className="text-3xl font-bold text-primary-600">{stats.accuracy}%</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-600 mb-1">Wagered</div>
          <div className="text-3xl font-bold text-gray-600">{stats.totalWagered} R$</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-600 mb-1">Net Profit</div>
          <div className={`text-3xl font-bold ${stats.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {stats.netProfit >= 0 ? '+' : ''}{stats.netProfit} R$
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex space-x-2 border-b">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 font-semibold ${
            filter === 'all'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          All ({stats.total})
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 font-semibold ${
            filter === 'pending'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Pending ({stats.pending})
        </button>
        <button
          onClick={() => setFilter('won')}
          className={`px-4 py-2 font-semibold ${
            filter === 'won'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Won ({stats.won})
        </button>
        <button
          onClick={() => setFilter('lost')}
          className={`px-4 py-2 font-semibold ${
            filter === 'lost'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Lost ({stats.lost})
        </button>
      </div>

      {/* Predictions List */}
      {filteredPredictions.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-600 text-lg mb-4">
            {filter === 'all' 
              ? "You haven't made any predictions yet"
              : `No ${filter} predictions`
            }
          </p>
          <Link to="/auctions" className="btn-primary">
            Browse Auctions
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPredictions.map((prediction) => (
            <div key={prediction.id} className="card hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                {/* Thumbnail */}
                <div className="flex-shrink-0 w-24 h-24 bg-gray-100 rounded overflow-hidden">
                  {prediction.lotImage ? (
                    <img
                      src={prediction.lotImage}
                      alt={prediction.lotTitle}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/96?text=No+Image';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <span className="text-2xl">📦</span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <Link 
                        to={`/auctions/${prediction.auctionId}`}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        {prediction.auctionTitle}
                      </Link>
                      <h3 className="font-bold text-lg">{prediction.lotTitle}</h3>
                      <p className="text-sm text-gray-600">SKU: {prediction.lotSku}</p>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadge(prediction.status)}`}>
                        {getOutcomeIcon(prediction.outcomeType)} {prediction.status.charAt(0).toUpperCase() + prediction.status.slice(1)}
                      </span>
                      {prediction.predictedOutsideRange && prediction.status === 'won' && (
                        <p className="text-xs text-orange-600 font-semibold mt-1">Beat Expert!</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm mb-2">
                    <div>
                      <span className="text-gray-600 block">Wagered:</span>
                      <span className="font-semibold">{prediction.wagerAmount} R$</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">Your Prediction:</span>
                      <span className="font-semibold text-primary-600">${prediction.predictedPrice}</span>
                    </div>
                    {prediction.actualPrice && (
                      <>
                        <div>
                          <span className="text-gray-600 block">Actual Price:</span>
                          <span className="font-semibold">${prediction.actualPrice}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 block">Difference:</span>
                          <span className={`font-semibold ${
                            Math.abs(prediction.predictedPrice - prediction.actualPrice) < prediction.actualPrice * 0.1
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`}>
                            ${Math.abs(prediction.predictedPrice - prediction.actualPrice).toFixed(2)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600 block">Result:</span>
                          {prediction.status === 'won' ? (
                            <span className="font-semibold text-green-600">
                              +{(prediction.ringersEarned || 0) - prediction.wagerAmount} R$
                            </span>
                          ) : (
                            <span className="font-semibold text-red-600">
                              -{prediction.wagerAmount} R$
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {prediction.multiplier && prediction.status === 'won' && (
                    <div className="mt-2 p-2 bg-green-50 rounded text-sm">
                      <span className="font-semibold text-green-800">
                        {prediction.multiplier.toFixed(1)}x multiplier
                      </span>
                      {prediction.outcomeType === 'outside_range_hit' && (
                        <span className="ml-2 text-orange-600">⚡ Beat the expert!</span>
                      )}
                    </div>
                  )}

                  <div className="mt-2 text-xs text-gray-500">
                    Predicted on {format(new Date(prediction.createdAt), 'MMM dd, yyyy h:mm a')}
                    {prediction.settledAt && (
                      <> • Settled on {format(new Date(prediction.settledAt), 'MMM dd, yyyy h:mm a')}</>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Predictions;